import { useState, useEffect } from 'react'
import { api } from '../lib/api'

export default function Billing({ user, business }) {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [upgrading, setUpgrading] = useState(false)

  useEffect(() => {
    loadStatus()
  }, [])

  async function loadStatus() {
    try {
      const data = await api.getSubscriptionStatus(business.id)
      setStatus(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function handleUpgrade() {
    setUpgrading(true)
    try {
      const { url } = await api.createCheckoutSession({
        user_id: user.id,
        business_id: business.id,
        email: user.email,
      })
      window.location.href = url
    } catch (err) {
      alert('Something went wrong. Please try again.')
      setUpgrading(false)
    }
  }

  async function handleManageBilling() {
    try {
      const { url } = await api.createBillingPortal(business.id)
      window.location.href = url
    } catch (err) {
      alert('Something went wrong. Please try again.')
    }
  }

  function getStatusDisplay() {
    if (!status?.subscription_status) return { label: 'Free Trial', color: '#4f46e5', bg: '#1a1a3e' }
    const map = {
      trialing: { label: '30-Day Free Trial', color: '#4f46e5', bg: '#1a1a3e' },
      active: { label: 'Active', color: '#22c55e', bg: '#0a2a14' },
      past_due: { label: 'Payment Past Due', color: '#f59e0b', bg: '#2a1f0a' },
      canceled: { label: 'Canceled', color: '#ef4444', bg: '#2a1a1a' },
    }
    return map[status.subscription_status] || { label: 'Free Trial', color: '#4f46e5', bg: '#1a1a3e' }
  }

  const statusDisplay = getStatusDisplay()
  const isActive = status?.subscription_status === 'active' || status?.subscription_status === 'trialing'
  const needsUpgrade = !status?.subscription_status || status?.subscription_status === 'canceled' || status?.subscription_status === 'trial'

  if (loading) return <div style={s.empty}>Loading billing...</div>

  return (
    <div>
      <h1 style={s.pageTitle}>Billing</h1>
      <p style={s.pageSub}>Manage your JobConfirmed subscription.</p>

      {/* Current plan card */}
      <div style={s.card}>
        <div style={s.cardTop}>
          <div>
            <div style={s.planName}>JobConfirmed Monthly</div>
            <div style={s.planPrice}>$39.99<span style={s.perMonth}>/month</span></div>
          </div>
          <span style={{...s.badge, color: statusDisplay.color, background: statusDisplay.bg}}>
            {statusDisplay.label}
          </span>
        </div>

        <div style={s.features}>
          <div style={s.feature}>✅ Automated appointment reminders</div>
          <div style={s.feature}>✅ No-show alerts</div>
          <div style={s.feature}>✅ Post-job review requests</div>
          <div style={s.feature}>✅ Unlimited jobs</div>
          <div style={s.feature}>✅ Mobile-friendly dashboard</div>
        </div>

        {needsUpgrade && (
          <button style={s.upgradeBtn} onClick={handleUpgrade} disabled={upgrading}>
            {upgrading ? 'Redirecting to checkout...' : 'Start 30-Day Free Trial'}
          </button>
        )}

        {isActive && (
          <button style={s.manageBtn} onClick={handleManageBilling}>
            Manage Billing
          </button>
        )}

        {status?.subscription_status === 'past_due' && (
          <button style={s.upgradeBtn} onClick={handleManageBilling}>
            Update Payment Method
          </button>
        )}
      </div>

      {/* Info box */}
      <div style={s.infoBox}>
        <p style={s.infoText}>
          💳 Your free trial lasts 30 days — no credit card required to start.
          You'll only be charged after the trial ends. Cancel anytime.
        </p>
      </div>
    </div>
  )
}

const s = {
  pageTitle: { fontSize: 28, fontWeight: 700, margin: '0 0 8px', color: '#fff', fontFamily: "'DM Sans', sans-serif" },
  pageSub: { color: '#6b6b80', fontSize: 14, margin: '0 0 32px', fontFamily: "'DM Sans', sans-serif" },
  card: {
    background: '#13131a', border: '1px solid #1e1e2e',
    borderRadius: 16, padding: '28px', maxWidth: 480,
    fontFamily: "'DM Sans', sans-serif",
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  planName: { color: '#a0a0b8', fontSize: 13, fontWeight: 600, marginBottom: 6 },
  planPrice: { color: '#fff', fontSize: 36, fontWeight: 700 },
  perMonth: { color: '#6b6b80', fontSize: 16, fontWeight: 400 },
  badge: { padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600 },
  features: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 },
  feature: { color: '#a0a0b8', fontSize: 14 },
  upgradeBtn: {
    width: '100%', background: '#4f46e5', color: '#fff', border: 'none',
    borderRadius: 8, padding: '13px 0', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  manageBtn: {
    width: '100%', background: 'none', color: '#a0a0b8',
    border: '1px solid #2a2a3a', borderRadius: 8, padding: '13px 0',
    fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  infoBox: {
    background: '#13131a', border: '1px solid #1e1e2e',
    borderRadius: 12, padding: '16px 20px', maxWidth: 480, marginTop: 16,
  },
  infoText: { color: '#6b6b80', fontSize: 13, margin: 0, lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" },
  empty: { color: '#6b6b80', padding: 40, fontFamily: "'DM Sans', sans-serif" },
}
